import { ChangeEvent, useEffect, useRef, useState } from "react"
import { Alert, Button, Form, Spinner } from "react-bootstrap"
import { readEmbeddedImageDescription } from "./image-description"

export type DraftArticleItem = {
 articleId: number; title: string; status?: string; topic: string; articleTypeName: string
 previewLink: string; editLink: string; date?: string; dateGmt?: string; updatedAt?: string; publicLink?: string
}
type DraftDetails = { articleId: number; title: string; modifiedGmt: string; version?: string; contentPreview: string }
type ImageData = { url: string; width: number; height: number; description: string }
type Props = {
 doFetch: (path: string, body?: any, method?: string) => Promise<Response>
 onUpdated: (item: DraftArticleItem) => void
 onEditing: () => void
 apiBase?: string
 allDrafts?: boolean
}

async function readImage(file: File): Promise<ImageData> {
 if (!["image/jpeg", "image/png", "image/gif", "image/webp"].includes(file.type) || file.size > 12 * 1024 * 1024) throw new Error("Vælg et JPEG-, PNG-, GIF- eller WebP-billede på højst 12 MB.")
 const [url, description] = await Promise.all([new Promise<string>((resolve, reject) => {
  const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.onerror = () => reject(new Error("Billedet kunne ikke læses.")); reader.readAsDataURL(file)
 }), readEmbeddedImageDescription(file)])
 return new Promise((resolve, reject) => {
  const image = new Image()
  image.onload = () => {
   const width = image.naturalWidth; const height = image.naturalHeight
   if (width < 1200 || width * height <= 300000) reject(new Error("Billedet skal være mindst 1.200 px bredt og indeholde mere end 300.000 pixels."))
   else resolve({ url, width, height, description })
  }
  image.onerror = () => reject(new Error("Billedfilen kunne ikke åbnes.")); image.src = url
 })
}

export default function ExistingDraftsWorkflow({ doFetch, onUpdated, onEditing, apiBase = "/eb-pristjek/articles", allDrafts = false }: Props) {
 const [items, setItems] = useState<DraftArticleItem[]>([])
 const [selectedId, setSelectedId] = useState("")
 const [details, setDetails] = useState<DraftDetails | null>(null)
 const [title, setTitle] = useState("")
 const [image, setImage] = useState<ImageData | null>(null)
 const [alt, setAlt] = useState("")
 const [loading, setLoading] = useState(true)
 const [loadingDetails, setLoadingDetails] = useState(false)
 const [readingImage, setReadingImage] = useState(false)
 const [saving, setSaving] = useState(false)
 const [error, setError] = useState("")
 const [seo, setSeo] = useState<{ title: string; description: string } | null>(null)
 const imageVersion = useRef(0)
 const altEdited = useRef(false)
 useEffect(() => {
  let active = true
  doFetch(`${apiBase}/drafts?limit=400`).then(async response => {
   const data = await response.json()
   if (!response.ok && response.status >= 400) throw new Error(data.error || "Kunne ikke hente kladder.")
   if (active) setItems((data.items || []).filter((item: DraftArticleItem) => item.status === "draft"))
  }).catch(err => { if (active) setError(err.message) }).finally(() => { if (active) setLoading(false) })
  return () => { active = false }
 }, [doFetch, apiBase])
 useEffect(() => {
  let active = true
  setDetails(null); setTitle(""); setImage(null); setAlt(""); setError(""); setReadingImage(false); imageVersion.current++
  altEdited.current = false
  if (!selectedId) { setLoadingDetails(false); return }
  setLoadingDetails(true)
  doFetch(`${apiBase}/drafts/${selectedId}?_=${Date.now()}-${Math.random().toString(36).slice(2)}`).then(async response => {
   const data = await response.json()
   if (response.status >= 400) throw new Error(data.error || "Kunne ikke hente kladden.")
   if (active) { setDetails(data); setTitle(data.title) }
  }).catch(err => { if (active) setError(err.message) }).finally(() => { if (active) setLoadingDetails(false) })
  return () => { active = false }
 }, [selectedId, doFetch, apiBase])
 function editing() { setSeo(null); onEditing() }
 async function imageChanged(event: ChangeEvent<HTMLInputElement>) {
  const file = event.target.files?.[0]; const version = ++imageVersion.current
  editing(); setImage(null); setError("")
  if (!altEdited.current) setAlt("")
  if (!file) { setReadingImage(false); return }
  setReadingImage(true)
  try {
   const data = await readImage(file)
   if (version === imageVersion.current) {
    setImage(data)
    if (!altEdited.current) setAlt(data.description)
   }
  }
  catch (err) { if (version === imageVersion.current) setError(err instanceof Error ? err.message : "Kunne ikke læse billedet.") }
  finally { if (version === imageVersion.current) setReadingImage(false) }
 }
 async function refreshDetails() {
  if (!details || saving || loadingDetails) return
  setLoadingDetails(true)
  try {
   const response = await doFetch(`${apiBase}/drafts/${details.articleId}?_=${Date.now()}-${Math.random().toString(36).slice(2)}`)
   const data = await response.json()
   if (response.status >= 400) throw new Error(data.error || "Kunne ikke hente kladden.")
   setDetails(data); setError("")
  } catch (err) { setError(err instanceof Error ? err.message : "Kunne ikke hente kladden.") }
  finally { setLoadingDetails(false) }
 }
 async function save() {
  if (!details || !image || saving) return
  setSaving(true); setError(""); setSeo(null); onEditing()
  try {
   const response = await doFetch(`${apiBase}/drafts/${details.articleId}/prepare`, {
    title: title.trim(), expectedModifiedGmt: details.modifiedGmt, ...(details.version ? { expectedVersion: details.version } : {}),
    featuredImageUrl: image.url, featuredImageAlt: alt.trim(), featuredImageWidth: image.width, featuredImageHeight: image.height,
   })
   const data = await response.json()
   if (response.status >= 400 || !data.item || !data.seo) throw new Error(data.error || "Kladden blev ikke opdateret.")
   setSeo(data.seo); setDetails(null); setSelectedId("")
   setItems(prev => prev.map(item => item.articleId === data.item.articleId ? data.item : item))
   onUpdated(data.item)
  } catch (err) { setError(err instanceof Error ? err.message : "Kunne ikke opdatere kladden.") }
  finally { setSaving(false) }
 }
 const previewLink = items.find(item => String(item.articleId) === selectedId)?.previewLink
 return <div>
  {error ? <Alert variant="danger">{error}
   {details && /Kladden er ændret/.test(error) ? <div className="mt-2">
    <Button variant="outline-danger" onClick={refreshDetails} disabled={loadingDetails || saving}>Hent seneste kladde</Button>
    <p className="mb-0 mt-2">Din overskrift og dit billede bevares. Gennemse den hentede artikeltekst før du trykker Opdater draft igen.</p>
   </div> : null}
  </Alert> : null}
  {loading ? <div><Spinner size="sm" animation="border" /> Henter kladder...</div> : <>
   <Form.Group className="mb-3" controlId="existingDraftSelection">
    <Form.Label>Vælg artikel</Form.Label>
    <Form.Select value={selectedId} disabled={saving} onChange={event => { editing(); setSelectedId(event.target.value) }}>
     <option value="">Vælg en kladde</option>
     {items.map(item => <option key={item.articleId} value={item.articleId}>{item.title || `Artikel ${item.articleId}`}</option>)}
    </Form.Select>
    <Form.Text className="d-block">{allDrafts ? "Alle kladder på det valgte medie." : "Kladder oprettet inden for de seneste 10 dage."}</Form.Text>
    {!items.length ? <Form.Text className="d-block">Der er ingen kladder.</Form.Text> : null}
   </Form.Group>
  </>}
  {loadingDetails ? <Spinner animation="border" size="sm" /> : null}
  {details ? <>
   <Form.Group className="mb-3" controlId="existingDraftTitle">
    <Form.Label>Artikeloverskrift</Form.Label>
    <Form.Control value={title} maxLength={180} disabled={saving} onChange={event => { editing(); setTitle(event.target.value) }} />
   </Form.Group>
   <details className="mb-3"><summary>Se artikeltekst</summary><p className="small mt-2">{details.contentPreview}</p></details>
   <Form.Group className="mb-3" controlId="existingDraftImage">
    <Form.Label>Artikelbillede</Form.Label>
    <Form.Control key={details.articleId} type="file" accept="image/jpeg,image/png,image/gif,image/webp" disabled={saving} onChange={imageChanged} />
    <Form.Text>Mindst 1.200 px bredt. Vælg et relevant billede, gerne i landskabsformat.</Form.Text>
   </Form.Group>
   {image ? <img src={image.url} alt={alt} className="img-fluid mb-3" style={{ maxHeight: 280, objectFit: "contain" }} /> : null}
   <Form.Group className="mb-3" controlId="existingDraftAlt">
    <Form.Label>Billedbeskrivelse</Form.Label>
    <Form.Control value={alt} maxLength={140} disabled={saving} onChange={event => { editing(); altEdited.current = true; setAlt(event.target.value) }} />
    <Form.Text>Udfyldes automatisk, hvis billedfilen indeholder en beskrivelse. Du kan rette teksten.</Form.Text>
   </Form.Group>
   <div className="d-flex flex-wrap gap-2">
    <Button onClick={save} disabled={saving || loadingDetails || readingImage || !image || title.trim().length < 5 || alt.trim().length < 12}>
     {saving ? <><Spinner animation="border" size="sm" className="me-2" />Opdaterer draft og SEO...</> : "Opdater draft"}
    </Button>
    {previewLink ? <Button as="a" href={previewLink} target="_blank" rel="noopener noreferrer" variant="outline-primary" title="Viser den senest gemte version i WordPress">Preview</Button> : null}
   </div>
  </> : null}
  {seo ? <Alert variant="success" className="mt-3"><strong>SEO-titel:</strong> {seo.title}<br /><strong>Metabeskrivelse:</strong> {seo.description}</Alert> : null}
 </div>
}
